import { Component, EventEmitter, HostListener, inject, Output, TemplateRef, ViewChild } from "@angular/core";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { HistoryService } from "../../services/history.service";
import { Helper } from "src/app/core/helpers/helper";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { debounceTime, distinctUntilChanged, Subject, Subscription, switchMap } from "rxjs";
import { GoogleMapsService } from "../../services/google-map.service";
import { MapBoxService } from "../../services/mapbox.service";
import { BookingService } from "../../services/booking.service";
import { BusinessType } from "src/app/core/constants/bookingConstant";
import { UserType } from "src/app/core/constants/constants";
import { CommonService } from "../../services/common.service";

@Component({
  selector: "app-delivery-booking-status-change-modal",
  templateUrl: "./delivery-booking-status-change-modal.component.html",
  styleUrl: "./delivery-booking-status-change-modal.component.scss",
  standalone: false
})
export class DeliveryBookingStatusChangeModalComponent {
  selectedCompletedAddressMapSearchIndex: number = -1;

  isCancelBooking: boolean = false;

  bookingDetails: any;
  countryDetails: any;

  completedAddressPredictions: Array<any> = [];
  completedAddressMapBoxSuggestions: Array<any> = [];
  cancelBookingReasonList: Array<any> = [];
  driverList: Array<any> = [];
  assignTo: UserType = UserType.DRIVER;
  completeBookingDetailsForm!: FormGroup;

  private completeAddressInputSubject: Subject<string> = new Subject<string>();
  private completeAddressInputSubscription!: Subscription;

  deliveryBookingStatusChangeConfirmationModalRef!: NgbModalRef;

  @Output() changeQcBookingStatusEvent = new EventEmitter();

  @ViewChild("deliveryBookingStatusChangeConfirmationModal") deliveryBookingStatusChangeConfirmationModal!: TemplateRef<any>;

  private bookingService = inject(BookingService);

  constructor(
    public modalService: NgbModal,
    private historyService: HistoryService,
    public helper: Helper,
    private googleMapsApiService: GoogleMapsService,
    private mapBoxService: MapBoxService,
    private commonService: CommonService
  ) {}

  /**
   * Opens the modal with booking details and initializes necessary data.
   *
   * @param bookingDetails The details of the booking.
   * @param countryDetails The details of the country.
   * @param isCancelBooking Flag indicating if the booking is being canceled.
   */
  async openModal(bookingDetails: any, countryDetails: any, isCancelBooking: boolean, assignTo: UserType = UserType.DRIVER): Promise<void> {
    this.bookingDetails = JSON.parse(JSON.stringify(bookingDetails));
    this.countryDetails = countryDetails;
    this.isCancelBooking = isCancelBooking;
    this.assignTo = assignTo;

    if (this.isCancelBooking) {
      await this.getCancellationReasonList();
    }

    this.initCompleteBookingDetailsForm();

    this.patchCompleteAddress();

    if (this.getDeliveryNextBookingStatus() === this.helper.BookingStatus.ASSIGNED) {
      if (bookingDetails.businessType === this.helper.BusinessType.SERVICE && !bookingDetails.quickCommerce.merchantDetail?.id && assignTo === UserType.DRIVER) {
        await this.findServiceDriver();
      } else if (bookingDetails.businessType === this.helper.BusinessType.SERVICE && assignTo === UserType.MERCHANT) {
        await this.findServiceMerchant();
      } else {
        await this.findDriver();
      }
    }

    if (this.getDeliveryNextBookingStatus() === this.helper.BookingStatus.ARRIVED_AT_DESTINATION && !this.isCancelBooking && !this.isBookingTypePickUp()) {
      this.initCompleteAddressAutoComplete();
    }

    this.deliveryBookingStatusChangeConfirmationModalRef = this.modalService.open(this.deliveryBookingStatusChangeConfirmationModal, { centered: true, backdrop: "static" });
    this.deliveryBookingStatusChangeConfirmationModalRef.result.then(
      () => {},
      () => {}
    );
  }

  /**
   * Closes the modal.
   */
  closeModal(): void {
    this.deliveryBookingStatusChangeConfirmationModalRef?.dismiss("Cross click"); // Close modal with reason 'Cross click'
  }

  isBookingTypePickUp(): boolean {
    return this.bookingDetails?.bookingType ? this.bookingDetails.bookingType === this.helper.PriceMode.PICKUP : false;
  }

  /**
   * Finds a driver for the booking.
   *
   * @returns A promise that resolves when the driver is found.
   */
  findDriver(): Promise<void> {
    return new Promise((resolve) => {
      const destinationAddresses = JSON.parse(JSON.stringify(this.bookingDetails.destinationAddresses.slice(-1)));

      const parameters: any = {
        cityId: this.bookingDetails?.cityId,
        pickupAddress: { latitude: this.bookingDetails.pickupAddress.latitude, longitude: this.bookingDetails.pickupAddress.longitude },
        destinationAddress: { latitude: destinationAddresses[0].latitude, longitude: destinationAddresses[0].longitude },
        businessType: this.bookingDetails?.businessType,
        isShowErrorToast: false,
        paymentMode: this.bookingDetails.bookingInvoice.paymentMode,
        merchantId: this.bookingDetails.quickCommerce.merchantDetail.id,
        bookingType: this.bookingDetails?.bookingType
      };

      if (this.bookingDetails?.vehicleTypeId) {
        parameters["vehicleTypeId"] = this.bookingDetails.vehicleTypeId;
      }
      this.bookingService.getNearestDriver(parameters).subscribe((response: any) => {
        this.driverList = response?.drivers ?? [];
        resolve();
      });
    });
  }

  findServiceDriver(): Promise<void> {
    return new Promise((resolve) => {
      const parameters: any = {
        bookingId: this.bookingDetails._id,
        isShowErrorToast: false
      };
      this.bookingService.findServiceProviders(parameters).subscribe((response: any) => {
        this.driverList = response?.drivers ?? [];
        resolve();
      });
    });
  }

  findServiceMerchant(): Promise<void> {
    return new Promise((resolve) => {
      const parameters: any = {
        bookingId: this.bookingDetails._id,
        isShowErrorToast: false
      };
      this.bookingService.findServiceMerchants(parameters).subscribe((response: any) => {
        this.driverList = response?.merchants ?? [];
        resolve();
      });
    });
  }

  /**
   * Patches the complete address in the form.
   */
  patchCompleteAddress(): void {
    const destinationAddresses = JSON.parse(JSON.stringify(this.bookingDetails?.destinationAddresses.slice(-1)));

    const address: any = destinationAddresses.length > 0 ? destinationAddresses[0] : this.bookingDetails.address;

    if (
      address &&
      (this.bookingDetails?.status === this.helper.BookingStatus.STARTED ||
        this.bookingDetails?.status === this.helper.BookingStatus.ARRIVED_NEAR_DESTINATION ||
        this.bookingDetails?.status === this.helper.BookingStatus.ARRIVED_AT_STOP)
    ) {
      this.completeBookingDetailsForm.patchValue({
        completeAddress: address.address,
        stopAddress: {
          address: address.address,
          latitude: address.latitude,
          longitude: address.longitude,
          city: address.city,
          countryCode: address.countryCode,
          country: address.country,
          postalCode: address.postalCode,
          note: "",
          placeId: address.placeId
        }
      });
    }
  }

  /**
   * Retrieves the list of cancellation reasons.
   *
   * @returns A promise that resolves when the cancellation reasons are retrieved.
   */
  getCancellationReasonList(): Promise<void> {
    return new Promise((resolve) => {
      const parameter = { type: this.helper.UserType.ADMIN, businessType: BusinessType.DELIVERY };
      this.commonService.getCancellationReasonList(parameter).subscribe((response: any) => {
        this.cancelBookingReasonList = response?.cancellationReasons?.length > 0 ? response.cancellationReasons : [];
        resolve();
      });
    });
  }

  /**
   * Initializes the form for completing booking details.
   */
  initCompleteBookingDetailsForm(): void {
    this.completeBookingDetailsForm = new FormGroup({
      note: new FormControl<string | null>(null)
    });

    if (this.getDeliveryNextBookingStatus() === this.helper.BookingStatus.ARRIVED_AT_DESTINATION && !this.isCancelBooking && !this.isBookingTypePickUp()) {
      this.completeBookingDetailsForm.addControl("completeAddress", new FormControl<string | null>(null, [Validators.required]));
      this.completeBookingDetailsForm.addControl("stopAddress", new FormControl<any>(null, [Validators.required]));
    }

    if (this.isCancelBooking) {
      this.completeBookingDetailsForm.addControl("cancellationReason", new FormControl<string | null>(null, [Validators.required]));
      this.completeBookingDetailsForm.addControl("specificReason", new FormControl<string | null>(null));
    }

    if (this.getDeliveryNextBookingStatus() === this.helper.BookingStatus.ASSIGNED && !this.isCancelBooking) {
      this.completeBookingDetailsForm.addControl("driverId", new FormControl<string | null>(null, [Validators.required]));
    }
  }

  /**
   * Gets the specific reason input control from the form.
   *
   * @returns The specific reason input control.
   */
  get specificReasonInput(): any {
    return this.completeBookingDetailsForm.get("specificReason");
  }

  isBookingTypePickup(): boolean {
    return this.bookingDetails.bookingType === this.helper.PriceMode.PICKUP;
  }

  /**
   * Gets the next booking status for Quick Commerce.
   *
   * @returns The next booking status.
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity, complexity
  getDeliveryNextBookingStatus(): number {
    if (
      this.bookingDetails?.quickCommerce?.merchantDetail?.id &&
      [this.helper.BookingStatus.MERCHANT_COMPLETED, this.helper.BookingStatus.PICKER_ACCEPTED, this.helper.BookingStatus.PICKER_COMPLETED].includes(
        this.bookingDetails?.quickCommerce?.status
      ) &&
      (this.bookingDetails?.status === this.helper.BookingStatus.REQUESTED || this.bookingDetails?.status === this.helper.BookingStatus.NO_DRIVER_FOUND) &&
      !this.isBookingTypePickup()
    ) {
      return this.helper.BookingStatus.ASSIGNED;
    } else if (
      this.bookingDetails?.businessType === BusinessType.SERVICE &&
      ((this.bookingDetails?.quickCommerce?.merchantDetail?.id && this.bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_ACCEPTED) ||
        !this.bookingDetails?.quickCommerce?.merchantDetail?.id) &&
      (this.bookingDetails?.status === this.helper.BookingStatus.REQUESTED || this.bookingDetails?.status === this.helper.BookingStatus.NO_DRIVER_FOUND)
    ) {
      return this.helper.BookingStatus.ASSIGNED;
    } else if (
      (this.bookingDetails?.businessType === BusinessType.TAXI || this.bookingDetails?.businessType === BusinessType.TAXI_METER || this.bookingDetails?.businessType === BusinessType.TOWING) &&
      (this.bookingDetails?.status === this.helper.BookingStatus.REQUESTED || this.bookingDetails?.status === this.helper.BookingStatus.NO_DRIVER_FOUND)
    ) {
      return this.helper.BookingStatus.ASSIGNED;
    } else if (
      this.bookingDetails?.quickCommerce?.merchantDetail?.id &&
      this.bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_COMPLETED &&
      this.isBookingTypePickup()
    ) {
      return this.helper.BookingStatus.ARRIVED_AT_DESTINATION;
    } else if (this.bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.REQUESTED) {
      return this.helper.BookingStatus.MERCHANT_ACCEPTED;
    } else if (this.bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_ACCEPTED) {
      return this.helper.BookingStatus.MERCHANT_PREPARING;
    } else if (this.bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_PREPARING) {
      return this.helper.BookingStatus.MERCHANT_COMPLETED;
    } else if (this.bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.PICKED) {
      return this.helper.BookingStatus.STARTED;
    } else if (this.bookingDetails?.status === this.helper.BookingStatus.STARTED || this.bookingDetails?.status === this.helper.BookingStatus.ARRIVED_NEAR_DESTINATION) {
      return this.helper.BookingStatus.ARRIVED_AT_DESTINATION;
    }
    return 0;
  }

  /**
   * Gets the button string for the current booking status.
   *
   * @returns The button string.
   */
  getDeliveryStatusButtonString(): string {
    if (
      this.bookingDetails?.quickCommerce?.merchantDetail?.id &&
      this.bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_COMPLETED &&
      (this.bookingDetails?.status === this.helper.BookingStatus.REQUESTED || this.bookingDetails?.status === this.helper.BookingStatus.NO_DRIVER_FOUND) &&
      !this.isBookingTypePickup()
    ) {
      return this.helper.translateService.instant("label-title.assign-deliveryman");
    } else if (
      this.bookingDetails?.quickCommerce?.merchantDetail?.id &&
      this.bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_COMPLETED &&
      this.isBookingTypePickup()
    ) {
      return this.helper.translateService.instant("label-title.complete");
    } else if (this.bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.REQUESTED) {
      return this.helper.translateService.instant("label-title.merchant-accept");
    } else if (this.bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_ACCEPTED) {
      return this.helper.translateService.instant("label-title.start-preparing");
    } else if (this.bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.MERCHANT_PREPARING) {
      return this.helper.translateService.instant("label-title.order-ready");
    } else if (this.bookingDetails?.quickCommerce?.status === this.helper.BookingStatus.PICKED) {
      return this.helper.translateService.instant("label-title.started");
    } else if (this.bookingDetails?.status === this.helper.BookingStatus.STARTED || this.bookingDetails?.status === this.helper.BookingStatus.ARRIVED_NEAR_DESTINATION) {
      return this.helper.translateService.instant("label-title.complete");
    }
    return "";
  }

  /**
   * Gets the string for canceling the booking.
   */
  getCancelBookingString(): void {
    return this.helper.translateService.instant("label-title.cancel");
  }

  /**
   * Initializes the autocomplete for the complete address input.
   */
  initCompleteAddressAutoComplete(): void {
    const params = this.countryDetails?.code2 ? { region: this.countryDetails?.code2 } : {};

    if (this.completeAddressInputSubscription) {
      this.completeAddressInputSubscription.unsubscribe();
    }
    this.completeAddressInputSubscription = this.completeAddressInputSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(async (input) => {
          this.completedAddressPredictions = [];
          return await this.googleMapsApiService.getPlacePredictions(input, params);
        })
      )
      .subscribe((predictions) => {
        this.completedAddressPredictions = predictions;
      });
  }

  /**
   * Handles changes to the complete address input.
   *
   * @param event The input change event.
   */
  onCompleteAddressInputChange(event: Event): void {
    if (this.helper.mapTypeGoogle()) {
      const inputElement = event.target as HTMLInputElement;
      const inputValue = inputElement.value.trim();

      this.completeAddressInputSubject.next(inputValue);
    } else if (this.helper.mapTypeMapBox()) {
      this.debouncedOnCompletedAddressSearchMapbox(event);
    }
  }

  debouncedOnCompletedAddressSearchMapbox = this.helper.debounce(this.onSearch.bind(this), 500);

  /**
   * Handles the search for addresses using MapBox.
   *
   * @param event The search event.
   */
  onSearch(event: any): void {
    const query = event.target.value;
    if (query.length > 2) {
      const code: string = this.countryDetails.code ?? "";
      this.mapBoxService.search(query, code).subscribe((response: any) => {
        this.completedAddressMapBoxSuggestions = response.features;
      });
    } else {
      this.completedAddressMapBoxSuggestions = [];
    }
  }

  /**
   * Checks if a completed address is selected.
   *
   * @param index The index of the address.
   * @returns True if the address is selected, false otherwise.
   */
  isCompletedAddressSelected(index: number): boolean {
    return this.selectedCompletedAddressMapSearchIndex === index;
  }

  /**
   * Handles the selection of a prediction from the autocomplete.
   *
   * @param prediction The selected prediction.
   */
  async onPredictionSelect(prediction: any): Promise<void> {
    this.completedAddressPredictions = [];
    const place = await this.googleMapsApiService.fetchFields(prediction);
    this.completeBookingDetailsForm.patchValue({
      completeAddress: place.formatted_address,
      stopAddress: this.extractAddressDetailsUsingGoogle(place, prediction)
    });
  }

  /**
   * Extracts address details using Google Maps API.
   *
   * @param place The place details from Google Maps.
   * @param prediction The selected prediction.
   * @returns The extracted address details.
   */
  extractAddressDetailsUsingGoogle(place: any, prediction: any): any {
    const getAddressComponent = (types: string[]): any => place.address_components.find((component: any) => types.some((type) => component.types.includes(type)));

    const cityComponent = getAddressComponent(["locality", "administrative_area_level_3"]);
    const city = cityComponent?.longText ?? "";

    const countryComponent = getAddressComponent(["country"]);
    const country = countryComponent?.longText ?? "";
    const countryCode = countryComponent?.shortText ?? "";

    const postalCodeComponent = getAddressComponent(["postal_code"]);
    const postalCode = postalCodeComponent?.longText ?? "";

    return {
      address: place.formatted_address,
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng(),
      city,
      countryCode,
      country,
      postalCode,
      note: "",
      placeId: prediction.place_id
    };
  }

  /**
   * Handles keyboard events for address selection.
   *
   * @param event The keyboard event.
   */
  @HostListener("document:keydown", ["$event"])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.helper.mapTypeGoogle()) {
      this.handleGoogleMapKeyboardEvent(event);
    } else if (this.helper.mapTypeMapBox()) {
      this.handleMapBoxKeyboardEvent(event);
    }
  }

  /**
   * Handles keyboard events for Google Maps address selection.
   *
   * @param event The keyboard event.
   */
  handleGoogleMapKeyboardEvent(event: KeyboardEvent): void {
    if (this.completedAddressPredictions.length > 0) {
      if (event.key === "ArrowDown") {
        this.selectedCompletedAddressMapSearchIndex = (this.selectedCompletedAddressMapSearchIndex + 1) % this.completedAddressPredictions.length;
      } else if (event.key === "ArrowUp") {
        this.selectedCompletedAddressMapSearchIndex =
          (this.selectedCompletedAddressMapSearchIndex - 1 + this.completedAddressPredictions.length) % this.completedAddressPredictions.length;
      } else if (
        event.key === "Enter" &&
        this.selectedCompletedAddressMapSearchIndex >= 0 &&
        this.selectedCompletedAddressMapSearchIndex < this.completedAddressPredictions.length
      ) {
        this.onPredictionSelect(this.completedAddressPredictions[this.selectedCompletedAddressMapSearchIndex]);
        this.selectedCompletedAddressMapSearchIndex = -1;
      }
    }
  }

  /**
   * Handles keyboard events for MapBox address selection.
   *
   * @param event The keyboard event.
   */
  handleMapBoxKeyboardEvent(event: KeyboardEvent): void {
    if (this.completedAddressMapBoxSuggestions.length > 0) {
      if (event.key === "ArrowDown") {
        this.selectedCompletedAddressMapSearchIndex = (this.selectedCompletedAddressMapSearchIndex + 1) % this.completedAddressMapBoxSuggestions.length;
      } else if (event.key === "ArrowUp") {
        this.selectedCompletedAddressMapSearchIndex =
          (this.selectedCompletedAddressMapSearchIndex - 1 + this.completedAddressMapBoxSuggestions.length) % this.completedAddressMapBoxSuggestions.length;
      } else if (
        event.key === "Enter" &&
        this.selectedCompletedAddressMapSearchIndex >= 0 &&
        this.selectedCompletedAddressMapSearchIndex < this.completedAddressMapBoxSuggestions.length
      ) {
        this.onSelect(this.completedAddressMapBoxSuggestions[this.selectedCompletedAddressMapSearchIndex]);
        this.selectedCompletedAddressMapSearchIndex = -1;
      }
    }
  }

  /**
   * Handles the selection of a suggestion from MapBox.
   *
   * @param suggestion The selected suggestion.
   */
  onSelect(suggestion: any): void {
    this.completeBookingDetailsForm.patchValue({
      completeAddress: suggestion.place_name,
      stopAddress: this.extractAddressDetailsFromMapBox(suggestion)
    });

    this.completedAddressMapBoxSuggestions = [];
  }

  /**
   * Extracts address details from a MapBox suggestion.
   *
   * @param suggestion The selected suggestion.
   * @returns The extracted address details.
   */
  extractAddressDetailsFromMapBox(suggestion: any): any {
    const getContextItem = (type: string): any => suggestion.context.find((item: any) => item.id.startsWith(type));

    const cityContext = getContextItem("place");
    const city = cityContext?.text ?? "";

    const countryContext = getContextItem("country");
    const country = countryContext?.text ?? "";
    const countryCode = countryContext?.short_code.toUpperCase() ?? "";

    const postalCodeContext = getContextItem("postcode");
    const postalCode = postalCodeContext?.text ?? "";

    return {
      address: suggestion.place_name,
      latitude: suggestion.geometry.coordinates[1],
      longitude: suggestion.geometry.coordinates[0],
      city,
      countryCode,
      country,
      postalCode,
      note: "",
      placeId: suggestion.id
    };
  }

  /**
   * Starts the button loader.
   */
  startBtnLoader(): void {
    if (this.helper.showBtnLoader[1] === true) {
      return;
    }

    this.helper.showBtnLoader[1] = true;
  }

  /**
   * Updates the booking status.
   */
  updateBookingStatus(): void {
    if (this.getDeliveryNextBookingStatus() === this.helper.BookingStatus.MERCHANT_ACCEPTED) {
      this.acceptMerchantBooking();
      return;
    }

    if (this.getDeliveryNextBookingStatus() === this.helper.BookingStatus.ASSIGNED) {
      this.reAssingDriver();
      return;
    }

    this.changeBookingStatus();
  }

  /**
   * Changes the booking status.
   */
  changeBookingStatus(): void {
    const parameters: any = { status: this.getDeliveryNextBookingStatus() };
    if (this.bookingDetails.confirmedDriver?.id) {
      parameters["driverId"] = this.bookingDetails.confirmedDriver.id;
    }
    if (this.bookingDetails.quickCommerce.merchantDetail?.id) {
      parameters["merchantId"] = this.bookingDetails.quickCommerce.merchantDetail.id;
    }

    if (this.getDeliveryNextBookingStatus() === this.helper.BookingStatus.ARRIVED_AT_DESTINATION && !this.isCancelBooking && !this.isBookingTypePickUp()) {
      if (this.completeBookingDetailsForm.invalid) {
        this.completeBookingDetailsForm.markAllAsTouched();
        return;
      }

      parameters["stopAddress"] = this.completeBookingDetailsForm.value.stopAddress;
    }

    parameters["note"] = this.completeBookingDetailsForm.value.note ?? "";

    this.startBtnLoader();

    this.bookingService.changeDeliveryBookingStatus(this.bookingDetails._id, parameters).subscribe((response: any) => {
      this.helper.showBtnLoader[1] = false;
      if (response) {
        this.closeModal();
        setTimeout(() => {
          if (
            this.getDeliveryNextBookingStatus() === this.helper.BookingStatus.ARRIVED_AT_DESTINATION &&
            this.getDeliveryNextBookingStatus() === this.helper.BookingStatus.STARTED
          ) {
            this.bookingDetails.status = this.getDeliveryNextBookingStatus();
          } else {
            this.bookingDetails.quickCommerce.status = this.getDeliveryNextBookingStatus();
          }
          this.changeQcBookingStatusEvent.emit(this.bookingDetails);
        }, 500);
      }
    });
  }

  /**
   * Sets validators for the specific reason input.
   */
  setSpecificReasonValidators(): void {
    if (this.completeBookingDetailsForm.value.cancellationReason === "Other") {
      this.completeBookingDetailsForm.get("specificReason")?.setValidators([Validators.required]);
    } else {
      this.completeBookingDetailsForm.get("specificReason")?.setValidators(null);
    }
    this.completeBookingDetailsForm.get("specificReason")?.updateValueAndValidity();
  }

  /**
   * Cancels the booking.
   */
  cancelBooking(): void {
    this.completeBookingDetailsForm.patchValue({
      specificReason: this.completeBookingDetailsForm.value.specificReason?.toString()?.trim()
    });

    this.setSpecificReasonValidators();

    if (this.completeBookingDetailsForm.invalid) {
      this.completeBookingDetailsForm.markAllAsTouched();
      return;
    }

    const parameters: any = {
      note: this.completeBookingDetailsForm.value.note ?? ""
    };

    if (this.completeBookingDetailsForm.value.cancellationReason === "Other") {
      parameters.cancellationReason = this.completeBookingDetailsForm.value.specificReason;
    } else {
      parameters.cancellationReason = this.completeBookingDetailsForm.value.cancellationReason;
    }

    this.startBtnLoader();

    this.historyService.cancelBooking(this.bookingDetails._id, parameters).subscribe((response: any) => {
      this.helper.showBtnLoader[1] = false;
      if (response) {
        this.closeModal();
        setTimeout(() => {
          this.bookingDetails.status = this.helper.BookingStatus.CANCELLED;
          this.changeQcBookingStatusEvent.emit(this.bookingDetails);
        }, 500);
      }
    });
  }

  /**
   * Accepts the merchant booking.
   */
  acceptMerchantBooking(): void {
    this.startBtnLoader();

    this.bookingService.acceptMerchantBooking(this.bookingDetails._id, { merchantId: this.bookingDetails.quickCommerce.merchantDetail.id }).subscribe((response: any) => {
      this.helper.showBtnLoader[1] = false;
      if (response) {
        this.closeModal();
        setTimeout(() => {
          this.bookingDetails.quickCommerce.status = this.helper.BookingStatus.MERCHANT_ACCEPTED;
          this.changeQcBookingStatusEvent.emit(this.bookingDetails);
        }, 500);
      }
    });
  }

  /**
   * Reassigns the driver for the booking.
   */
  reAssingDriver(): void {
    if (this.completeBookingDetailsForm.invalid) {
      this.completeBookingDetailsForm.markAllAsTouched();
      return;
    }

    if (this.bookingDetails.businessType === this.helper.BusinessType.SERVICE) {
      this.bookingService
        .assignMerchants({
          bookingId: this.bookingDetails._id,
          entityType: this.assignTo,
          typeId: this.completeBookingDetailsForm.value.driverId
        })
        .subscribe((response: any) => {
          if (response) {
            this.bookingDetails.status = this.helper.BookingStatus.ASSIGNED;
            this.changeQcBookingStatusEvent.emit(this.bookingDetails);
            this.closeModal();
          }
        });
    } else {
      this.bookingService
        .reAssingDriver({
          bookingId: this.bookingDetails._id,
          driverId: this.completeBookingDetailsForm.value.driverId
        })
        .subscribe((response: any) => {
          if (response) {
            this.bookingDetails.status = this.helper.BookingStatus.ASSIGNED;
            this.changeQcBookingStatusEvent.emit(this.bookingDetails);
            this.closeModal();
          }
        });
    }
  }
}
